<template>
  <div
    ref="dataTable"
    class="internal-data-table"
    :class="[tableClassName]"
    :style="selectionCellStyle"
  >
    <div
      ref="tableBody"
      class="internal-data-table__main"
      :class="{
        'fixed-header': fixedHeader,
        'fixed-height': tableHeight,
        'show-shadow': showShadow,
        'table-fixed': fixedHeaders.length,
        'hoverable': !noHover,
        'border-cell': borderCell,
      }"
    >
      <table :id="tableNodeId">
        <colgroup>
          <col
            v-for="(header, index) in headersForRender"
            :key="index"
            :style="getColStyle(header)"
          >
        </colgroup>
        <slot v-if="slots['customize-headers']" name="customize-headers" />
        <thead
          v-else-if="headersForRender.length && !hideHeader"
          class="internal-data-table__header"
          :class="[headerClassName]"
        >
          <tr>
            <th
              v-for="(header, index) in headersForRender"
              :key="index"
              :class="[{
                sortable: header.sortable,
                'none': header.sortable && header.sortType === 'none',
                'desc': header.sortable && header.sortType === 'desc',
                'asc': header.sortable && header.sortType === 'asc',
                'shadow': header.value === lastFixedColumn,
                'selection-cell': header.value === 'checkbox',
              }, typeof headerItemClassName === 'string' ? headerItemClassName : headerItemClassName(header as Header, index + 1)]"
              :style="getFixedDistance(header.value)"
              :aria-sort="getAriaSort(header)"
              @click.stop="(header.sortable && header.sortType) ? updateSortField(header.value, header.sortType) : null"
            >
              <MultipleSelectCheckBox
                v-if="header.text === 'checkbox' && !isSingleSelectable"
                :key="multipleSelectStatus"
                :status="multipleSelectStatus"
                @change="toggleSelectAll"
              />
              <template
                v-else-if="header.text === 'checkbox' && isSingleSelectable"
              />
              <span
                v-else
                class="header"
                :class="`direction-${headerTextDirection}`"
              >
                <FontIcon
                  v-if="header.sortable"
                  :key="header.sortType ? header.sortType : 'none'"
                  class="sortType-icon"
                  :icon-name="getSortIcon(header.sortType)"
                />
                <slot
                  v-if="slots[`header-${header.value}`]"
                  :name="`header-${header.value}`"
                  v-bind="header"
                />
                <slot
                  v-else-if="slots[`header-${header.value.toLowerCase()}`]"
                  :name="`header-${header.value.toLowerCase()}`"
                  v-bind="header"
                />
                <slot v-else-if="slots.header" name="header" v-bind="header" />
                <span v-else class="header-text" :title="header.text">
                  {{ header.text }}
                </span>
                <Tooltip
                  v-if="header.tooltipText"
                  :description="header.tooltipText"
                  :position="TooltipPosition.TOP"
                >
                  <button
                    type="button"
                    class="header-tooltip"
                    :aria-label="header.tooltipText"
                    @click.stop
                  >
                    <FontIcon :icon-name="TUNDR_ICONS.INFO" />
                  </button>
                </Tooltip>
                <span
                  v-if="multiSort && isMultiSorting(header.value)"
                  class="multi-sort__number"
                >
                  {{ getMultiSortNumber(header.value) }}
                </span>
              </span>
            </th>
          </tr>
        </thead>
        <slot v-if="ifHasBodySlot" name="body" v-bind="pageItems" />
        <tbody
          v-else-if="headerColumns.length"
          class="internal-data-table__body"
          :class="{'row-alternation': alternating}"
        >
          <slot
            name="body-prepend"
            v-bind="{
              items: pageItems,
              pagination: {
                isFirstPage,
                isLastPage,
                currentPaginationNumber,
                maxPaginationNumber,
                nextPage,
                prevPage
              },
              headers: headersForRender
            }"
          />
          <template v-for="(item, index) in pageItems" :key="index">
            <Tooltip
              :description="getRowDisabledReason(item, index + 1)"
              :position="TooltipPosition.TOP"
            >
              <tr
                :class="[{'even-row': (index + 1) % 2 === 0},
                         getRowClassName(item, index + 1)]"
                @click="($event) => {
                  clickRow(item, 'single', $event);
                  selectRowOnClick(item, index + 1);
                  clickRowToExpand && updateExpandingItemIndexList(index + prevPageEndIndex, item, $event);
                }"
                @dblclick="($event) => {clickRow(item, 'double', $event)}"
                @contextmenu="($event) => {contextMenuRow(item, $event)}"
                @auxclick="($event) => {middleClickRow(item, $event)}"
                :tabindex="keyboardActivatableRows ? 0 : undefined"
                @keydown="($event) => {
                  keyboardActivatableRows && activateRowFromKeyboard(item, $event);
                }"
              >
                <!-- biome-ignore lint/a11y/useKeyWithClickEvents: cell-scoped pointer handling inside an already-clickable row; a role per cell would add one tab stop per column -->
                <td
                  v-for="(column, i) in headerColumns"
                  :key="i"
                  :title="getCellTitle(column, item)"
                  :style="getFixedDistance(column, 'td')"
                  :class="[{
                    'shadow': column === lastFixedColumn,
                    'can-expand': column === 'expand',
                    'selection-cell': column === 'checkbox',
                  }, typeof bodyItemClassName === 'string' ? bodyItemClassName : bodyItemClassName(column, index + 1), `direction-${bodyTextDirection}`]"
                  @click="($event) => {
                    if (column === 'expand') updateExpandingItemIndexList(index + prevPageEndIndex, item, $event);
                    else if (column === 'checkbox' && !isSingleSelectable) $event.stopPropagation();
                  }"
                >
                  <slot
                    v-if="slots[`item-${column}`]"
                    :name="`item-${column}`"
                    v-bind="item"
                  />
                  <slot
                    v-else-if="slots[`item-${column.toLowerCase()}`]"
                    :name="`item-${column.toLowerCase()}`"
                    v-bind="item"
                  />
                  <template v-else-if="column === 'expand'">
                    <i
                      class="expand-icon"
                      :class="{'expanding': expandingItemIndexList.includes(prevPageEndIndex + index)}"
                    />
                  </template>
                  <template v-else-if="column === 'checkbox'">
                    <div
                      v-if="isSingleSelectable"
                      :class="selectionCheckboxClass"
                    >
                      <FontIcon
                        :icon-name="TUNDR_ICONS.CHECK_CIRCLE_SOLID"
                        :class="[
                          singleSelectCheckIconClass,
                          !item[column] && singleSelectCheckIconHiddenClass,
                        ]"
                      />
                    </div>
                    <SingleSelectCheckBox
                      v-else-if="!isSingleSelectable"
                      :checked="item[column]"
                      @change="toggleSelectItem(item)"
                    />
                  </template>
                  <slot
                    v-else-if="slots.item"
                    name="item"
                    v-bind="{column, item}"
                  />
                  <template v-else>
                    {{ generateColumnContent(column, item) }}
                  </template>
                </td>
              </tr>
            </Tooltip>
            <tr
              v-if="ifHasExpandSlot && expandingItemIndexList.includes(index + prevPageEndIndex)"
              :class="[{'even-row': (index + 1) % 2 === 0},
                       typeof bodyExpandRowClassName === 'string' ? bodyExpandRowClassName : bodyExpandRowClassName(item, index + 1)]"
            >
              <td :colspan="headersForRender.length" class="expand">
                <LoadingLine v-if="item.expandLoading" class="expand-loading" />
                <slot name="expand" v-bind="item" />
              </td>
            </tr>
          </template>
          <slot
            name="body-append"
            v-bind="{
              items: pageItems,
              pagination: {
                isFirstPage,
                isLastPage,
                currentPaginationNumber,
                maxPaginationNumber,
                nextPage,
                prevPage,
                updatePage
              },
              headers: headersForRender
            }"
          />
        </tbody>
      </table>
      <div v-if="loading" class="internal-data-table__loading">
        <div class="internal-data-table__loading-mask "></div>
        <div class="loading-entity">
          <slot v-if="ifHasLoadingSlot" name="loading" />
          <Loading v-else></Loading>
        </div>
      </div>

      <div
        v-if="!pageItems.length && !loading"
        class="internal-data-table__message"
      >
        <slot name="empty-message"> {{ emptyMessage }} </slot>
      </div>
    </div>
    <div v-if="!hideFooter" class="internal-data-table__footer">
      <div v-if="!hideRowsPerPage" class="pagination__rows-per-page">
        {{ rowsPerPageMessage }}
        <RowsSelector
          v-model="rowsPerPageRef"
          :rows-items="rowsItemsComputed"
        />
      </div>
      <div class="pagination__items-index">
        {{ `${currentPageFirstIndex}–${currentPageLastIndex}` }}
        {{ rowsOfPageSeparatorMessage }} {{ totalItemsLength }}
      </div>
      <slot
        v-if="ifHasPaginationSlot"
        name="pagination"
        v-bind="{
          isFirstPage,
          isLastPage,
          currentPaginationNumber,
          maxPaginationNumber,
          nextPage,
          prevPage,
        }"
      />
      <PaginationArrows
        v-else
        :is-first-page="isFirstPage"
        :is-last-page="isLastPage"
        @click-next-page="nextPage"
        @click-prev-page="prevPage"
      >
        <template v-if="buttonsPagination" #buttonsPagination>
          <ButtonsPagination
            :current-pagination-number="currentPaginationNumber"
            :max-pagination-number="maxPaginationNumber"
            @update-page="updatePage"
          />
        </template>
      </PaginationArrows>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    useSlots,
    computed,
    toRefs,
    ref,
    watch,
    provide,
    onMounted,
  } from 'vue';

  import { FontIcon, Tooltip, TooltipPosition } from '@tundr/ds-components';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';

  import MultipleSelectCheckBox from './MultipleSelectCheckBox.vue';
  import SingleSelectCheckBox from './SingleSelectCheckBox.vue';
  import RowsSelector from './RowsSelector.vue';
  import Loading from './Loading.vue';
  import LoadingLine from './LoadingLine.vue';
  import ButtonsPagination from './ButtonsPagination.vue';
  import PaginationArrows from './PaginationArrows.vue';
  import {
    selectionCheckboxClass,
    singleSelectCheckIconClass,
    singleSelectCheckIconHiddenClass,
  } from './InternalDataTable.css';

  import useClickRow from '../hooks/useClickRow';
  import useMiddleClickRow from '../hooks/useMiddleClickRow';
  import useExpandableRow from '../hooks/useExpandableRow';
  import useFixedColumn from '../hooks/useFixedColumn';
  import useHeaders from '../hooks/useHeaders';
  import usePageItems from '../hooks/usePageItems';
  import usePagination from '../hooks/usePagination';
  import useRows from '../hooks/useRows';
  import useServerOptions from '../hooks/useServerOptions';
  import useTotalItems from '../hooks/useTotalItems';

  import { DEFAULT_CHECKBOX_COLUMN_WIDTH } from '../constants';
  import type { InternalDataTableProps } from '../internal-data-table.types';
  import type { Header, Item } from '../types/main';
  import type { HeaderForRender } from '../types/internal';

  import { generateColumnContent } from '../utils';

  const getSortIcon = (sortType: HeaderForRender['sortType']): TUNDR_ICONS => {
    if (sortType === 'desc') return TUNDR_ICONS.SORT_DESCENDING;
    if (sortType === 'asc') return TUNDR_ICONS.SORT_ASCENDING;
    return TUNDR_ICONS.SORTABLE;
  };

  const getAriaSort = (
    header: HeaderForRender,
  ): 'ascending' | 'descending' | 'none' | undefined => {
    if (!header.sortable) return undefined;
    if (header.sortType === 'asc') return 'ascending';
    if (header.sortType === 'desc') return 'descending';
    return 'none';
  };

  const hasItemSlot = (column: string): boolean =>
    !!slots[`item-${column}`] ||
    !!slots[`item-${column.toLowerCase()}`] ||
    !!slots.item;

  const getCellTitle = (column: string, item: Item): string | undefined => {
    if (column === 'expand' || column === 'checkbox' || hasItemSlot(column)) {
      return undefined;
    }
    const content = generateColumnContent(column, item);
    return content != null && content !== '' ? String(content) : undefined;
  };

  const props = withDefaults(defineProps<InternalDataTableProps>(), {
    alternating: false,
    buttonsPagination: false,
    checkboxColumnWidth: null,
    currentPage: 1,
    emptyMessage: 'No Available Data',
    expandColumnWidth: 36,
    filterOptions: null,
    fixedExpand: false,
    fixedHeader: true,
    fixedCheckbox: false,
    fixedIndex: false,
    headerTextDirection: 'left',
    bodyTextDirection: 'left',
    hideFooter: false,
    hideRowsPerPage: false,
    hideHeader: false,
    indexColumnWidth: 60,
    itemsSelected: null,
    maxItemsSelectable: null,
    loading: false,
    rowsPerPage: 25,
    rowsItems: () => [25, 50, 100],
    rowsPerPageMessage: 'rows per page:',
    searchField: '',
    searchValue: '',
    serverOptions: null,
    serverItemsLength: 0,
    showIndex: false,
    sortBy: '',
    sortType: 'asc',
    multiSort: false,
    tableMinHeight: 180,
    tableHeight: null,
    themeColor: '#42b883',
    tableClassName: '',
    headerClassName: '',
    headerItemClassName: '',
    bodyRowClassName: '',
    bodyExpandRowClassName: '',
    bodyItemClassName: '',
    noHover: false,
    borderCell: false,
    mustSort: false,
    rowsOfPageSeparatorMessage: 'of',
    clickEventType: 'single',
    clickRowToExpand: false,
    keyboardActivatableRows: false,
    tableNodeId: '',
    showIndexSymbol: '#',
    preventContextMenuRow: true,
  });

  const {
    tableNodeId,
    clickEventType,
    bodyTextDirection,
    checkboxColumnWidth,
    currentPage,
    expandColumnWidth,
    filterOptions,
    fixedCheckbox,
    fixedExpand,
    fixedHeader,
    fixedIndex,
    headers,
    headerTextDirection,
    indexColumnWidth,
    items,
    itemsSelected,
    loading,
    mustSort,
    multiSort,
    rowsItems,
    rowsPerPage,
    searchField,
    searchValue,
    serverItemsLength,
    serverOptions,
    showIndex,
    sortBy,
    sortType,
    tableHeight,
    tableMinHeight,
    themeColor,
    rowsOfPageSeparatorMessage,
    showIndexSymbol,
    preventContextMenuRow,
    tableClassName,
    headerClassName,
    headerItemClassName,
    bodyRowClassName,
    bodyExpandRowClassName,
    bodyItemClassName,
    disabledReason,
    alternating,
    noHover,
    borderCell,
    hideHeader,
    hideFooter,
    hideRowsPerPage,
    rowsPerPageMessage,
    emptyMessage,
    buttonsPagination,
    clickRowToExpand,
    keyboardActivatableRows,
  } = toRefs(props);

  const selectionCellStyle = computed(() => ({
    '--internal-table-selection-cell-width': `${checkboxColumnWidth.value ?? DEFAULT_CHECKBOX_COLUMN_WIDTH}px`,
  }));

  // style related computed variables
  const tableHeightPx = computed(() =>
    tableHeight.value ? `${tableHeight.value}px` : null,
  );
  const tableMinHeightPx = computed(() => `${tableMinHeight.value}px`);

  // global style related variable
  provide('themeColor', themeColor.value);

  // slot
  const slots = useSlots();
  const ifHasPaginationSlot = computed(() => !!slots.pagination);
  const ifHasLoadingSlot = computed(() => !!slots.loading);
  const ifHasExpandSlot = computed(() => !!slots.expand);
  const ifHasBodySlot = computed(() => !!slots.body);

  // global dataTable $ref
  const dataTable = ref();
  const tableBody = ref();
  provide('dataTable', dataTable);

  // fixed-columns shadow
  const showShadow = ref(false);
  onMounted(() => {
    tableBody.value?.addEventListener('scroll', () => {
      showShadow.value = (tableBody.value?.scrollLeft || 0) > 0;
    });
  });

  const emits = defineEmits([
    'clickRow',
    'middleclickRow',
    'contextmenuRow',
    'selectRow',
    'deselectRow',
    'expandRow',
    'updateSort',
    'updateFilter',
    'update:itemsSelected',
    'update:serverOptions',
    'updatePageItems',
    'updateTotalItems',
    'selectAll',
  ]);

  const isMultipleSelectable = computed(
    (): boolean => itemsSelected.value !== null,
  );
  const isSingleSelectable = computed(
    (): boolean => isMultipleSelectable.value && props.maxItemsSelectable === 1,
  );
  const isServerSideMode = computed(
    (): boolean => serverOptions.value !== null,
  );

  const {
    serverOptionsComputed,
    updateServerOptionsPage,
    updateServerOptionsSort,
    updateServerOptionsRowsPerPage,
  } = useServerOptions(serverOptions, multiSort, emits);

  const {
    clientSortOptions,
    headerColumns,
    headersForRender,
    updateSortField,
    isMultiSorting,
    getMultiSortNumber,
  } = useHeaders(
    showIndexSymbol,
    checkboxColumnWidth,
    expandColumnWidth,
    fixedCheckbox,
    fixedExpand,
    fixedIndex,
    headers,
    ifHasExpandSlot,
    indexColumnWidth,
    isMultipleSelectable,
    isServerSideMode,
    mustSort,
    serverOptionsComputed,
    showIndex,
    sortBy,
    sortType,
    multiSort,
    updateServerOptionsSort,
    emits,
  );

  const { rowsItemsComputed, rowsPerPageRef, updateRowsPerPage } = useRows(
    isServerSideMode,
    rowsItems,
    serverOptions,
    rowsPerPage,
  );

  const {
    totalItems,
    selectItemsComputed,
    totalItemsLength,
    toggleSelectAll,
    toggleSelectItem,
  } = useTotalItems(
    clientSortOptions,
    filterOptions,
    isServerSideMode,
    items,
    itemsSelected,
    searchField,
    searchValue,
    serverItemsLength,
    multiSort,
    isSingleSelectable,
    emits,
  );

  const ROW_NOT_SELECTABLE_CLASS = 'not-selectable-row';
  const ROW_DISABLED_CLASS = 'disabled-row';

  const getRowClassName = (item: Item, rowNumber: number): string =>
    typeof bodyRowClassName.value === 'string'
      ? bodyRowClassName.value
      : (bodyRowClassName.value?.(item, rowNumber) ?? '');

  const isRowDisabled = (item: Item, rowNumber: number): boolean => {
    const rowClassName = getRowClassName(item, rowNumber);

    return (
      rowClassName.includes(ROW_NOT_SELECTABLE_CLASS) ||
      rowClassName.includes(ROW_DISABLED_CLASS)
    );
  };

  const getRowDisabledReason = (
    item: Item,
    rowNumber: number,
  ): string | undefined =>
    disabledReason.value && isRowDisabled(item, rowNumber)
      ? disabledReason.value
      : undefined;

  const selectRowOnClick = (item: Item, rowNumber: number): void => {
    if (!isMultipleSelectable.value) return;

    if (isRowDisabled(item, rowNumber)) return;

    toggleSelectItem(item);
  };

  const {
    currentPaginationNumber,
    maxPaginationNumber,
    isLastPage,
    isFirstPage,
    nextPage,
    prevPage,
    updatePage,
    updateCurrentPaginationNumber,
  } = usePagination(
    currentPage,
    isServerSideMode,
    loading,
    totalItemsLength,
    rowsPerPageRef,
    serverOptions,
    updateServerOptionsPage,
  );

  const {
    currentPageFirstIndex,
    currentPageLastIndex,
    multipleSelectStatus,
    pageItems,
  } = usePageItems(
    currentPaginationNumber,
    isMultipleSelectable,
    isServerSideMode,
    items,
    rowsPerPageRef,
    selectItemsComputed,
    showIndex,
    totalItems,
    totalItemsLength,
  );

  const prevPageEndIndex = computed(() => {
    if (currentPaginationNumber.value === 0) return 0;
    return (currentPaginationNumber.value - 1) * rowsPerPageRef.value;
  });

  const {
    expandingItemIndexList,
    updateExpandingItemIndexList,
    clearExpandingItemIndexList,
  } = useExpandableRow(pageItems, prevPageEndIndex, emits);

  const { fixedHeaders, lastFixedColumn, fixedColumnsInfos } =
    useFixedColumn(headersForRender);

  const { clickRow } = useClickRow(
    clickEventType,
    isMultipleSelectable,
    showIndex,
    emits,
  );

  const { middleClickRow } = useMiddleClickRow(emits);

  const ROW_ACTIVATION_KEYS = ['Enter', ' '];

  const activateRowFromKeyboard = (item: Item, $event: KeyboardEvent) => {
    if (!ROW_ACTIVATION_KEYS.includes($event.key)) return;
    // Links, buttons and inputs inside a cell own their own keyboard
    // behaviour: only a keypress landing on the row itself activates the row.
    if ($event.target !== $event.currentTarget) return;
    $event.preventDefault();
    clickRow(item, 'single', $event);
  };

  const contextMenuRow = (item: Item, $event: MouseEvent) => {
    if (preventContextMenuRow.value) $event.preventDefault();
    emits('contextmenuRow', item, $event);
  };

  // template style generation function
  const getColStyle = (header: HeaderForRender): string | undefined => {
    const width = header.width ?? (fixedHeaders.value.length ? 100 : null);
    if (width) return `width: ${width}px; min-width: ${width}px;`;
    return undefined;
  };

  const getFixedDistance = (column: string, type: 'td' | 'th' = 'th') => {
    if (!fixedHeaders.value.length) return undefined;
    const columInfo = fixedColumnsInfos.value.find(
      (info) => info.value === column,
    );
    if (columInfo) {
      return `left: ${columInfo.distance}px;z-index: ${type === 'th' ? 3 : 1};position: sticky;`;
    }
    return undefined;
  };

  watch(loading, (newVal, oldVal) => {
    if (serverOptionsComputed.value) {
      // in server-side mode, turn to next page when api request finished.
      if (newVal === false && oldVal === true) {
        updateCurrentPaginationNumber(serverOptionsComputed.value.page);
        clearExpandingItemIndexList();
      }
    }
  });

  watch(rowsPerPageRef, (value) => {
    if (!isServerSideMode.value) {
      updatePage(1);
    } else {
      updateServerOptionsRowsPerPage(value);
    }
  });

  watch([searchValue, filterOptions], () => {
    if (!isServerSideMode.value) {
      updatePage(1);
    }
  });

  watch(
    [
      currentPaginationNumber,
      clientSortOptions,
      searchField,
      searchValue,
      filterOptions,
    ],
    () => {
      clearExpandingItemIndexList();
    },
    { deep: true },
  );

  watch(
    pageItems,
    (value) => {
      emits('updatePageItems', value);
    },
    { deep: true },
  );

  watch(
    totalItems,
    (value) => {
      emits('updateTotalItems', value);
    },
    { deep: true },
  );

  defineExpose({
    currentPageFirstIndex,
    currentPageLastIndex,
    clientItemsLength: totalItemsLength,
    maxPaginationNumber,
    currentPaginationNumber,
    isLastPage,
    isFirstPage,
    nextPage,
    prevPage,
    updatePage,
    rowsPerPageOptions: rowsItemsComputed,
    rowsPerPageActiveOption: rowsPerPageRef,
    updateRowsPerPageActiveOption: updateRowsPerPage,
  });
</script>

<style>
  :root {
    /*table*/
    --internal-table-border: 1px solid #e0e0e0;
    --internal-table-row-border: 1px solid #e0e0e0;
    /*header-row*/
    --internal-table-header-font-size: 12px;
    --internal-table-header-height: 36px;
    --internal-table-header-font-color: #373737;
    --internal-table-header-background-color: #fff;
    /*header-item*/
    --internal-table-header-item-padding: 0px 10px;
    /*body-row*/
    --internal-table-body-row-height: 36px;
    --internal-table-body-row-font-size: 12px;

    --internal-table-body-row-font-color: #212121;
    --internal-table-body-row-background-color: #fff;

    --internal-table-body-row-hover-font-color: #212121;
    --internal-table-body-row-hover-background-color: #eee;

    --internal-table-body-even-row-font-color: #212121;
    --internal-table-body-even-row-background-color: #fafafa;
    /*body-item*/
    --internal-table-body-item-padding: 0px 10px;
    /*footer*/
    --internal-table-footer-background-color: #fff;
    --internal-table-footer-font-color: #212121;
    --internal-table-footer-font-size: 12px;
    --internal-table-footer-padding: 0px 5px;
    --internal-table-footer-height: 36px;
    /**footer-rowsPerPage**/
    --internal-table-rows-per-page-selector-width: auto;
    --internal-table-rows-per-page-selector-option-padding: 5px;
    --internal-table-rows-per-page-selector-z-index: auto;
    /*message*/
    --internal-table-message-font-color: #212121;
    --internal-table-message-font-size: 12px;
    --internal-table-message-padding: 20px;
    /*loading-mask*/
    --internal-table-loading-mask-background-color: #fff;
    --internal-table-loading-mask-opacity: 0.5;
    /*scroll-bar*/
    --internal-table-scrollbar-track-color: #fff;
    --internal-table-scrollbar-color: #fff;
    --internal-table-scrollbar-thumb-color: #c1c1c1;
    --internal-table-scrollbar-corner-color: #fff;
    /*buttons-pagination*/
    --internal-table-buttons-pagination-border: 1px solid #e0e0e0;
  }
</style>

<style scoped>
  .internal-data-table {
    border: var(--internal-table-border);
    position: relative;
    box-sizing: border-box;
  }

  .internal-data-table__main {
    border: none;
    width: 100%;
    overflow: auto;
    background-color: var(--internal-table-body-row-background-color);
  }
  .internal-data-table__main::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: var(--internal-table-scrollbar-track-color);
  }
  .internal-data-table__main::-webkit-scrollbar {
    width: 7px;
    height: 7px;
    background-color: var(--internal-table-scrollbar-color);
  }
  .internal-data-table__main::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: var(--internal-table-scrollbar-thumb-color);
  }
  .internal-data-table__main::-webkit-scrollbar-corner {
    background-color: var(--internal-table-scrollbar-corner-color);
  }
  .internal-data-table__main.table-fixed table {
    table-layout: fixed;
  }
  .internal-data-table__main.show-shadow th.shadow::after,
  .internal-data-table__main.show-shadow td.shadow::after {
    box-shadow: inset 6px 0 5px -3px rgba(0, 0, 0, 0.2);
  }
  .internal-data-table__main.fixed-header th {
    position: sticky;
    top: 0;
    z-index: 2;
  }
  .internal-data-table__main.hoverable tr:hover td {
    background-color: var(--internal-table-body-row-hover-background-color);
    color: var(--internal-table-body-row-hover-font-color);
  }
  .internal-data-table__main.hoverable
    .internal-data-table__body.row-alternation
    .even-row:hover
    td {
    color: var(--internal-table-body-row-hover-font-color);
    background-color: var(--internal-table-body-row-hover-background-color);
  }
  .internal-data-table__main.border-cell .internal-data-table__header th {
    border-right: var(--internal-table-row-border);
  }
  .internal-data-table__main.border-cell
    .internal-data-table__header
    th:last-of-type {
    border-right: none;
  }
  .internal-data-table__main.border-cell .internal-data-table__body td {
    border-right: var(--internal-table-row-border);
  }
  .internal-data-table__main.border-cell
    .internal-data-table__body
    td:last-of-type {
    border-right: none;
  }

  .internal-data-table__body.row-alternation tr.even-row td {
    color: var(--internal-table-body-even-row-font-color);
    background-color: var(--internal-table-body-even-row-background-color);
  }

  table {
    border-collapse: initial;
    display: table;
    width: 100%;
    border-spacing: 0;
    margin: 0px;
  }
  .internal-data-table__header,
  internal-data-table__body {
    position: relative;
  }

  .internal-data-table__header tr {
    font-size: var(--internal-table-header-font-size);
    border: none;
    height: var(--internal-table-header-height);
  }

  .internal-data-table__header th {
    background-color: var(--internal-table-header-background-color);
    color: var(--internal-table-header-font-color);
    border: none;
    border-bottom: var(--internal-table-row-border);
    padding: var(--internal-table-header-item-padding);
    position: relative;
  }
  .internal-data-table__header th .header {
    display: flex;
    align-items: center;
  }
  .internal-data-table__header th .header.direction-left {
    justify-content: flex-start;
  }
  .internal-data-table__header th .header.direction-center {
    justify-content: center;
  }
  .internal-data-table__header th .header.direction-right {
    justify-content: flex-end;
  }
  .internal-data-table__header th.sortable {
    cursor: pointer;
  }
  .internal-data-table__header th.sortable .sortType-icon {
    margin-right: var(--internal-table-header-sort-icon-gap, 8px);
    color: var(
      --internal-table-header-sort-icon-color,
      var(--internal-table-header-font-color)
    );
    flex-shrink: 0;
  }
  .internal-data-table__header th.sortable.none .sortType-icon {
    color: var(
      --internal-table-header-sort-icon-inactive-color,
      var(--internal-table-header-font-color)
    );
  }
  .internal-data-table__header th .header .header-tooltip {
    display: inline-flex;
    align-items: center;
    margin-left: var(--internal-table-header-tooltip-gap, 8px);
    color: var(
      --internal-table-header-tooltip-icon-color,
      var(--internal-table-header-font-color)
    );
    flex-shrink: 0;
    cursor: pointer;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
  }
  .internal-data-table__header th.sortable .multi-sort__number {
    border-radius: 50%;
    height: 1.5em;
    width: 1.5em;
    line-height: 1.5em;
    margin-left: 4px;
    background-color: var(--internal-table-header-font-color);
    color: var(--internal-table-header-background-color);
  }

  .internal-data-table__header th.shadow::after,
  .internal-data-table__body td.shadow::after {
    pointer-events: none;
    content: "";
    width: 36px;
    display: inline-block;
    height: 100%;
    position: absolute;
    top: 0px;
    right: -36px;
    box-shadow: none;
  }

  .internal-data-table__body tr {
    height: var(--internal-table-body-row-height);
    color: var(--internal-table-body-row-font-color);
    font-size: var(--internal-table-body-row-font-size);
  }
  .internal-data-table__body tr:last-child td {
    border-bottom: none;
  }
  .internal-data-table__body tr:first-child td {
    border-bottom: var(--internal-table-row-border);
  }

  .internal-data-table__body td {
    padding: var(--internal-table-body-item-padding);
    background-color: var(--internal-table-body-row-background-color);
    border: none;
    border-bottom: var(--internal-table-row-border);
  }

  /*
                 * The selection control carries its own padding, so the cell drops the
                 * horizontal one to stay at the checkbox column's minimum width.
                 */
  .internal-data-table__header th.selection-cell,
  .internal-data-table__body td.selection-cell {
    padding-left: 0;
    padding-right: 0;
    width: var(--internal-table-selection-cell-width);
    max-width: var(--internal-table-selection-cell-width);
  }
  .internal-data-table__body td.direction-left {
    text-align: left;
  }
  .internal-data-table__body td.direction-center {
    text-align: center;
  }
  .internal-data-table__body td.direction-right {
    text-align: right;
  }
  .internal-data-table__body td {
    position: relative;
  }
  .internal-data-table__body td .expand-icon {
    border: solid;
    border-color: var(--internal-table-body-row-font-color);
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 3px;
    transform: rotate(-45deg);
    transition: 0.2s;
  }
  .internal-data-table__body td .expand-icon.expanding {
    transform: rotate(45deg);
  }

  .internal-data-table__body td.expand {
    position: relative;
  }
  .internal-data-table__body td.expand .expand-loading {
    position: absolute;
    top: 0px;
    left: 0px;
  }

  .internal-data-table__body td.can-expand {
    cursor: pointer;
  }

  .internal-data-table__footer {
    background-color: var(--internal-table-footer-background-color);
    color: var(--internal-table-footer-font-color);
    border-top: var(--internal-table-row-border);
    font-size: var(--internal-table-footer-font-size);
    height: var(--internal-table-footer-height);
    padding: var(--internal-table-footer-padding);
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .internal-data-table__footer .pagination__rows-per-page {
    display: flex;
    align-items: center;
  }
  .internal-data-table__footer .pagination__items-index {
    margin: 0px 20px 0px 10px;
  }

  .internal-data-table__message {
    color: var(--internal-table-message-font-color);
    font-size: var(--internal-table-message-font-size);
    padding: var(--internal-table-message-padding);
    text-align: center;
  }

  .internal-data-table__loading {
    z-index: 3;
    overflow: hidden;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0px;
    left: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .internal-data-table__loading .loading-entity {
    z-index: 1;
  }

  .internal-data-table__loading-mask {
    background-color: var(--internal-table-loading-mask-background-color);
    opacity: var(--internal-table-loading-mask-opacity);
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0px;
    left: 0px;
    z-index: 1;
  }

  .internal-data-table__main {
    min-height: v-bind(tableMinHeightPx);
  }

  .internal-data-table__main.fixed-height {
    height: v-bind(tableHeightPx);
  }
</style>
